"use client";

import { DualRangeSlider } from "@/components/common/DualRangeSlider";
import { useEffect, useState, useCallback } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useSearchParams } from "next/navigation";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import Price from "@/components/common/Price";

function debounce<T extends readonly unknown[]>(
  func: (...args: T) => void,
  wait: number
): (...args: T) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: T) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export default function PriceRangeFilter({
  minPrice,
  maxPrice,
}: {
  minPrice?: string;
  maxPrice?: string;
}) {
  const searchParams = useSearchParams();
  const { fastPush } = useFastRouter();

  const effectiveMin = Number(minPrice ?? "100");
  const effectiveMax = Number(maxPrice ?? "5000");

  const startPriceParam = searchParams.get("startPrice");
  const endPriceParam = searchParams.get("endPrice");

  const [range, setRange] = useState<[number, number]>([
    startPriceParam ? Number(startPriceParam) : effectiveMin,
    endPriceParam ? Number(endPriceParam) : effectiveMax,
  ]);
  const [open, setOpen] = useState(true);

  const updateUrl = useCallback(
    debounce((newRange: [number, number]) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set("startPrice", String(newRange[0]));
      params.set("endPrice", String(newRange[1]));
      params.set("page", "1"); // reset page
      fastPush(`?${params.toString()}`);
    }, 300),
    [searchParams, fastPush]
  );

  const handleRangeChange = (newRange: [number, number]) => {
    setRange(newRange);
    updateUrl(newRange);
  };

  // Keep slider synced with URL or reset to full range if no params
  useEffect(() => {
    if (startPriceParam && endPriceParam) {
      setRange([Number(startPriceParam), Number(endPriceParam)]);
    } else {
      // No params → reset to full range
      setRange([effectiveMin, effectiveMax]);
    }
  }, [startPriceParam, endPriceParam, effectiveMin, effectiveMax]);

  // Hide price range component if min and max are the same
  if (effectiveMin === effectiveMax) {
    return null;
  }

  return (
    <div className="mb-4">
      <div
        onClick={() => setOpen(!open)}
        className="flex items-center justify-between cursor-pointer"
      >
        <h3 className="mt-2 font-[500] text-md">Price Range</h3>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>

      {open && (
        <>
          <div className="flex justify-between font-[500] text-sm mb-4 mt-2">
            <span>
              <Price amount={range[0]} />
            </span>
            <span>
              {" "}
              <Price amount={range[1]} />
            </span>
          </div>
          <DualRangeSlider
            value={range}
            onChange={handleRangeChange}
            min={effectiveMin}
            max={effectiveMax}
          />
        </>
      )}
    </div>
  );
}
